import { useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    DialogContent,
    Divider,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon } from "@mui/icons-material";
import { useStore } from "../../../store/store";
import { useCreateRecord } from "../../../api/useCreateRecord";
import { useUpdateRecord } from "../../../api/useUpdateRecord";
import { useDeleteRecord } from "../../../api/useDeleteRecord";
import { useRecords } from "../../../api/useRecords";
import { WEEK_DAY_LABELS } from "../../../utils/reservedSlotsConstants";
import { SlotForm } from "./SlotForm";
import { ReservedSlot, Clinic, TherapistJunction, SlotFormData } from "../types";

const RESERVED_SLOTS_ENTITY = "reserved_slots";

const normalizeTime = (time: string) => {
    if (!time) return "";
    const parts = time.split(":");
    const normalizedTime = `${parts[0]}:${parts[1]}`;
    return normalizedTime;
};

const EMPTY_FORM: SlotFormData = { week_day: "", time: "", therapist: undefined, clinic: "", weekly_anchor_date: null, biweekly_anchor_date: null };

type Service = {
    id: number;
    name?: string;
};

type ServiceProvided = {
    id: number;
    service?: Service;
    date_begin: string;
    session_periodicity: string;
    session_quantity: number;
};

type Treatment = {
    theurapists_involved_in_treamtent?: Array<TherapistJunction>;
};

type ReservedSlotsDialogProps = {
    serviceProvided: ServiceProvided;
    treatment: Treatment;
};

export const ReservedSlotsDialog = ({ serviceProvided, treatment }: ReservedSlotsDialogProps) => {
    const { appDialog, notifications } = useStore();
    const [editingSlotId, setEditingSlotId] = useState<number | null>(null);
    const [editForm, setEditForm] = useState<SlotFormData>(EMPTY_FORM);
    const [newSlotForm, setNewSlotForm] = useState<SlotFormData>(EMPTY_FORM);
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);
    const [showNewForm, setShowNewForm] = useState(false);

    const { data: reservedSlotsResponse, isLoading: isLoadingSlots, refetch } = useRecords(
        RESERVED_SLOTS_ENTITY,
        "*,therapist.*,clinic.*",
        {
            extraOptions: {
                filter: {
                    services_provided: { _eq: serviceProvided.id },
                },
            },
            enabled: !!serviceProvided.id,
        }
    );

    const { data: clinicsData } = useRecords("clinics", "*");

    const reservedSlots = (reservedSlotsResponse?.items ?? []) as Array<ReservedSlot>;
    const sortedReservedSlots = [...reservedSlots].sort((a, b) => a.week_day - b.week_day);
    const clinics = (clinicsData?.items ?? []) as Array<Clinic>;

    const createReservedSlot = useCreateRecord({ queryInvalidatePrefix: "reserved_slots" });
    const updateReservedSlot = useUpdateRecord({ queryInvalidatePrefix: "reserved_slots" });
    const deleteReservedSlot = useDeleteRecord({ queryInvalidatePrefix: "reserved_slots" });

    const isWeekly = serviceProvided.session_periodicity === "weekly";
    const isBiweekly = serviceProvided.session_periodicity === "monthly" && serviceProvided.session_quantity === 2;
    const therapistsFromTreatment = treatment?.theurapists_involved_in_treamtent ?? [];

    const validateForm = (form: SlotFormData): string | null => {
        if (form.week_day === "" || !form.time || form.clinic === "") {
            return "Preencha todos os campos obrigatórios.";
        }
        if (isWeekly && !form.weekly_anchor_date) {
            return "Selecione a data âncora semanal.";
        }
        if (isBiweekly && !form.biweekly_anchor_date) {
            return "Selecione a data âncora quinzenal.";
        }
        return null;
    };

    const prepareSlotData = (form: SlotFormData) => {
        const formattedWeeklyAnchorDate = form.weekly_anchor_date ? format(form.weekly_anchor_date, "yyyy-MM-dd") : null;
        const formattedBiweeklyAnchorDate = form.biweekly_anchor_date ? format(form.biweekly_anchor_date, "yyyy-MM-dd") : null;
        return {
            week_day: form.week_day,
            time: form.time,
            therapist: form.therapist?.id ?? null,
            clinic: form.clinic,
            weekly_anchor_date: isWeekly ? formattedWeeklyAnchorDate : null,
            biweekly_anchor_date: isBiweekly ? formattedBiweeklyAnchorDate : null,
        };
    };

    const handleCreate = async () => {
        const validationError = validateForm(newSlotForm);
        if (validationError) {
            notifications.setNotification({ isOpen: true, message: validationError, severity: "warning" });
            return;
        }
        setIsCreating(true);
        try {
            await createReservedSlot.mutateAsync({
                entity: RESERVED_SLOTS_ENTITY,
                data: {
                    services_provided: serviceProvided.id,
                    ...prepareSlotData(newSlotForm),
                },
            });
            notifications.setNotification({ isOpen: true, message: "Horário reservado criado com sucesso.", severity: "success" });
            setNewSlotForm(EMPTY_FORM);
            setShowNewForm(false);
            refetch();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errors?.[0]?.message ?? "Erro ao criar horário reservado.";
            notifications.setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        } finally {
            setIsCreating(false);
        }
    };

    const handleStartEdit = (slot: ReservedSlot) => {
        setEditingSlotId(slot.id);
        setEditForm({
            week_day: slot.week_day,
            time: normalizeTime(slot.time),
            therapist: slot.therapist,
            clinic: slot.clinic?.id ?? "",
            weekly_anchor_date: slot.weekly_anchor_date ? parseISO(slot.weekly_anchor_date) : null,
            biweekly_anchor_date: slot.biweekly_anchor_date ? parseISO(slot.biweekly_anchor_date) : null,
        });
    };

    const handleCancelEdit = () => {
        setEditingSlotId(null);
        setEditForm(EMPTY_FORM);
    };

    const handleUpdate = async () => {
        if (!editingSlotId) return;
        const validationError = validateForm(editForm);
        if (validationError) {
            notifications.setNotification({ isOpen: true, message: validationError, severity: "warning" });
            return;
        }
        setIsUpdating(true);
        try {
            await updateReservedSlot.mutateAsync({
                entity: RESERVED_SLOTS_ENTITY,
                id: editingSlotId,
                data: prepareSlotData(editForm),
            });
            notifications.setNotification({ isOpen: true, message: "Horário reservado atualizado com sucesso.", severity: "success" });
            setEditingSlotId(null);
            setEditForm(EMPTY_FORM);
            refetch();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errors?.[0]?.message ?? "Erro ao atualizar horário reservado.";
            notifications.setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async (slotId: number) => {
        setIsDeleting(slotId);
        try {
            await deleteReservedSlot.mutateAsync({ entity: RESERVED_SLOTS_ENTITY, id: slotId });
            notifications.setNotification({ isOpen: true, message: "Horário reservado eliminado com sucesso.", severity: "success" });
            refetch();
        } catch (error: any) {
            const errorMessage = error?.response?.data?.errors?.[0]?.message ?? "Erro ao eliminar horário reservado.";
            notifications.setNotification({ isOpen: true, message: errorMessage, severity: "error" });
        } finally {
            setIsDeleting(null);
        }
    };

    const handleShowNewForm = () => {
        const initialDate = serviceProvided.date_begin ? parseISO(serviceProvided.date_begin) : null;
        setNewSlotForm({
            ...EMPTY_FORM,
            weekly_anchor_date: isWeekly ? initialDate : null,
            biweekly_anchor_date: isBiweekly ? initialDate : null,
        });
        setShowNewForm(true);
    };

    return (
        <Box>
            <DialogContent>
                <Divider sx={{ mb: 4 }} />

                <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: "#f5f5f5" }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Serviço Prestado (Contexto)
                    </Typography>
                    <Box display="flex" gap={4} flexWrap="wrap">
                        <Box>
                            <Typography variant="caption" color="text.secondary">Serviço</Typography>
                            <Typography variant="body2" fontWeight="bold">{serviceProvided.service?.name}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Data Início</Typography>
                            <Typography variant="body2">{serviceProvided.date_begin}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Periodicidade</Typography>
                            <Typography variant="body2">{serviceProvided.session_periodicity}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">Qtd. Sessões</Typography>
                            <Typography variant="body2">{serviceProvided.session_quantity}</Typography>
                        </Box>
                    </Box>
                </Paper>

                <Typography variant="subtitle1" color="primary" fontWeight="bold" mb={2}>
                    Horários Reservados
                </Typography>

                {isLoadingSlots && <CircularProgress size={24} />}

                {!isLoadingSlots && reservedSlots.length === 0 && !showNewForm && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Sem horários reservados. Adicione um horário para reservar um slot permanente.
                    </Typography>
                )}

                {sortedReservedSlots.map((slot) => (
                    <Box
                        key={slot.id}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={2}
                        mb={2}
                        p={2}
                        sx={{ backgroundColor: "#fafafa", borderRadius: 2, border: "1px solid #e0e0e0" }}
                    >
                        {editingSlotId === slot.id ? (
                            <>
                                <SlotForm
                                    form={editForm}
                                    setForm={setEditForm}
                                    disabled={isUpdating}
                                    clinics={clinics}
                                    serviceId={serviceProvided.service?.id}
                                    therapistsFromTreatment={therapistsFromTreatment}
                                    isWeekly={isWeekly}
                                    isBiweekly={isBiweekly}
                                />
                                <IconButton onClick={handleUpdate} color="primary" size="small" disabled={isUpdating}>
                                    {isUpdating ? <CircularProgress size={20} /> : <SaveIcon />}
                                </IconButton>
                                <IconButton onClick={handleCancelEdit} color="secondary" size="small" disabled={isUpdating}>
                                    <CloseIcon />
                                </IconButton>
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ minWidth: 100 }}>
                                    {slot.clinic?.name ?? "-"}
                                </Typography>
                                <Typography variant="body2" sx={{ minWidth: 140 }}>
                                    {WEEK_DAY_LABELS[slot.week_day]}
                                </Typography>
                                <Typography variant="body2" sx={{ minWidth: 60 }}>
                                    {normalizeTime(slot.time)}
                                </Typography>
                                {slot.therapist && (
                                    <Typography variant="body2" sx={{ minWidth: 150 }}>
                                        {slot.therapist.first_name} {slot.therapist.last_name}
                                    </Typography>
                                )}
                                {isWeekly && slot.weekly_anchor_date && (
                                    <Typography variant="body2" sx={{ minWidth: 100 }}>
                                        Âncora: {format(parseISO(slot.weekly_anchor_date), "dd/MM/yyyy")}
                                    </Typography>
                                )}
                                {isBiweekly && slot.biweekly_anchor_date && (
                                    <Typography variant="body2" sx={{ minWidth: 100 }}>
                                        Âncora: {format(parseISO(slot.biweekly_anchor_date), "dd/MM/yyyy")}
                                    </Typography>
                                )}
                                <Box sx={{ ml: "auto" }}>
                                    <IconButton onClick={() => handleStartEdit(slot)} color="primary" size="small" disabled={!!editingSlotId}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(slot.id)} color="error" size="small" disabled={isDeleting === slot.id}>
                                        {isDeleting === slot.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                                    </IconButton>
                                </Box>
                            </>
                        )}
                    </Box>
                ))}

                {showNewForm && (
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={2}
                        mb={2}
                        p={2}
                        sx={{ backgroundColor: "#e8f5e9", borderRadius: 2, border: "1px solid #a5d6a7" }}
                    >
                        <SlotForm
                            form={newSlotForm}
                            setForm={setNewSlotForm}
                            disabled={isCreating}
                            clinics={clinics}
                            serviceId={serviceProvided.service?.id}
                            therapistsFromTreatment={therapistsFromTreatment}
                            isWeekly={isWeekly}
                            isBiweekly={isBiweekly}
                        />
                        <IconButton onClick={handleCreate} color="primary" size="small" disabled={isCreating}>
                            {isCreating ? <CircularProgress size={20} /> : <SaveIcon />}
                        </IconButton>
                        <IconButton onClick={() => { setShowNewForm(false); setNewSlotForm(EMPTY_FORM); }} color="secondary" size="small" disabled={isCreating}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                )}

                {!showNewForm && (
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleShowNewForm}
                        variant="outlined"
                        size="small"
                        disabled={!!editingSlotId}
                    >
                        Adicionar Horário
                    </Button>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: 4 }}>
                <Button onClick={() => appDialog.toggleIsOpen()} color="secondary" startIcon={<CloseIcon />}>
                    Fechar
                </Button>
            </DialogActions>
        </Box>
    );
};

